import React, { useState, useEffect } from "react";
import { Input, Form, Col, Row, Button, Select, Alert } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import RequestHandler from "util/RequestHandler";

const { Option } = Select;

const formItemLayout = {
  labelCol: { xs: { span: 24 }, md: { span: 24 }, lg: { span: 8 } },
  wrapperCol: { xs: { span: 24 }, md: { span: 24 }, lg: { span: 12 } },
};

const MaterialCategoryUpdateForm = (props) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const [level1Categories, setLevel1Categories] = useState([]);
  const [level2Categories, setLevel2Categories] = useState([]);
  const [level3Categories, setLevel3Categories] = useState([]);
  const [level4Categories, setLevel4Categories] = useState([]);

  const [selectedLevel1Id, setSelectedLevel1Id] = useState(null);
  const [selectedLevel2Id, setSelectedLevel2Id] = useState(null);
  const [selectedLevel3Id, setSelectedLevel3Id] = useState(null);
  const [selectedLevel4Id, setSelectedLevel4Id] = useState(null);

  const [currentLevelText, setCurrentLevelText] = useState("Yükleniyor...");

  const getCategories = (parentId, level) => {
    let filter = "";
    if (level === 1) {
      filter = "ParentId eq null";
    } else {
      if (!parentId) return Promise.resolve(null);
      filter = `ParentId eq ${parentId}`;
    }

    return RequestHandler.get(`/MaterialCategory?$filter=${filter}`).then((response) => {
      if (response) {
        if (level === 1) setLevel1Categories(response.value);
        if (level === 2) setLevel2Categories(response.value);
        if (level === 3) setLevel3Categories(response.value);
        if (level === 4) setLevel4Categories(response.value);
      }
      return response;
    });
  };

  const getCategoryDetail = (id) => {
    return RequestHandler.get(`/MaterialCategory/GetById/${id}`);
  };

  const updateLevelText = (l1, l2, l3, l4) => {
    if (l4) setCurrentLevelText("Kategori (Seviye 5)");
    else if (l3) setCurrentLevelText("Kategori (Seviye 4)");
    else if (l2) setCurrentLevelText("Kategori (Seviye 3)");
    else if (l1) setCurrentLevelText("Kategori (Seviye 2)");
    else setCurrentLevelText("Kategori (Seviye 1)");
  };

  useEffect(() => {
    if (props.data) {
      form.setFieldsValue({
        id: props.data.id,
        name: props.data.name,
        code: props.data.code,
      });
      resolveHierarchy(props.data);
    }
  }, [props.data]);

  const resolveHierarchy = async (currentData) => {
    await getCategories(null, 1);

    if (!currentData.parentId) {
      setCurrentLevelText("Kategori (Seviye 1)");
      return;
    }

    const ancestors = [];
    let currentId = currentData.parentId;

    while (currentId) {
      const detail = await getCategoryDetail(currentId);
      if (!detail) break;
      ancestors.unshift({ id: detail.id, parentId: detail.parentId });
      currentId = detail.parentId;
    }

    const formValues = {};
    for (let i = 0; i < ancestors.length; i++) {
      const levelKey = `level${i + 1}Id`;
      formValues[levelKey] = ancestors[i].id;

      if (i === 0) {
        // Level 1 zaten yüklendi
      } else {
        await getCategories(ancestors[i - 1].id, i + 1);
      }
    }

    const setters = [setSelectedLevel1Id, setSelectedLevel2Id, setSelectedLevel3Id, setSelectedLevel4Id];
    for (let i = 0; i < ancestors.length && i < 4; i++) {
      setters[i](ancestors[i].id);
    }

    if (ancestors.length < 4) {
      await getCategories(ancestors[ancestors.length - 1].id, ancestors.length + 1);
    }

    form.setFieldsValue(formValues);
    updateLevelText(
      ancestors[0]?.id,
      ancestors[1]?.id,
      ancestors[2]?.id,
      ancestors[3]?.id
    );
  };

  const handleLevel1Change = (value) => {
    setSelectedLevel1Id(value);
    setSelectedLevel2Id(null);
    setSelectedLevel3Id(null);
    setSelectedLevel4Id(null);
    setLevel2Categories([]);
    setLevel3Categories([]);
    setLevel4Categories([]);
    form.setFieldsValue({ level2Id: null, level3Id: null, level4Id: null });

    if (value) getCategories(value, 2);
    updateLevelText(value, null, null, null);
  };

  const handleLevel2Change = (value) => {
    setSelectedLevel2Id(value);
    setSelectedLevel3Id(null);
    setSelectedLevel4Id(null);
    setLevel3Categories([]);
    setLevel4Categories([]);
    form.setFieldsValue({ level3Id: null, level4Id: null });

    if (value) getCategories(value, 3);
    updateLevelText(selectedLevel1Id, value, null, null);
  };

  const handleLevel3Change = (value) => {
    setSelectedLevel3Id(value);
    setSelectedLevel4Id(null);
    setLevel4Categories([]);
    form.setFieldsValue({ level4Id: null });

    if (value) getCategories(value, 4);
    updateLevelText(selectedLevel1Id, selectedLevel2Id, value, null);
  };

  const handleLevel4Change = (value) => {
    setSelectedLevel4Id(value);
    updateLevelText(selectedLevel1Id, selectedLevel2Id, selectedLevel3Id, value);
  };

  const onFinish = (values) => {
    let parentIdToSend = null;

    if (values.level4Id) parentIdToSend = values.level4Id;
    else if (values.level3Id) parentIdToSend = values.level3Id;
    else if (values.level2Id) parentIdToSend = values.level2Id;
    else if (values.level1Id) parentIdToSend = values.level1Id;

    if (parentIdToSend === props.data.id) {
      return;
    }

    const finalValues = {
      id: props.data.id,
      name: values.name,
      code: values.code,
      ParentId: parentIdToSend,
    };

    setLoading(true);
    RequestHandler.put(`/MaterialCategory`, finalValues).then((response) => {
      setLoading(false);
      if (response.success) {
        if (props.onSuccess) props.onSuccess();
      }
    });
  };

  const filterSelf = (list) => list.filter((x) => x.id !== props.data?.id);

  return (
    <Form
      {...formItemLayout}
      form={form}
      name="MaterialUpdate"
      onFinish={onFinish}
    >
      <Row>
        <Col span={24}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Row justify="center" style={{ marginBottom: 20 }}>
            <Col span={20}>
              <Alert
                message={`Mevcut Durum: ${currentLevelText}`}
                type="warning"
                showIcon
              />
            </Col>
          </Row>

          <Form.Item label="Seviye 1 (Ana Kategori)" name="level1Id">
            <Select
              placeholder="Yok (Ana Kategori Yap)"
              allowClear
              onChange={handleLevel1Change}
            >
              {filterSelf(level1Categories).map((cat) => (
                <Option key={cat.id} value={cat.id}>{cat.name}</Option>
              ))}
            </Select>
          </Form.Item>

          {selectedLevel1Id && (
            <Form.Item label="Seviye 2" name="level2Id">
              <Select
                placeholder="Yok (Seviye 2 Yap)"
                allowClear
                onChange={handleLevel2Change}
              >
                {filterSelf(level2Categories).map((cat) => (
                  <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {selectedLevel2Id && (
            <Form.Item label="Seviye 3" name="level3Id">
              <Select
                placeholder="Yok (Seviye 3 Yap)"
                allowClear
                onChange={handleLevel3Change}
              >
                {filterSelf(level3Categories).map((cat) => (
                  <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {selectedLevel3Id && (
            <Form.Item label="Seviye 4" name="level4Id">
              <Select
                placeholder="Yok (Seviye 4 Yap)"
                allowClear
                onChange={handleLevel4Change}
              >
                {filterSelf(level4Categories).map((cat) => (
                  <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="Kategori Adı"
            name="name"
            rules={[{ required: true, message: "Lütfen isim giriniz." }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Kategori Kodu" name="code">
            <Input />
          </Form.Item>

          <Row justify="end">
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                htmlType="submit"
                loading={loading}
              >
                Güncelle
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default MaterialCategoryUpdateForm;
